import { createSelector } from 'reselect'
import { first, memoize } from 'lodash-es'
import {
  pipe,
  map as Rmap,
  flatten,
  head,
  sortBy,
  pathOr,
  concat,
  filter,
  groupBy,
  values,
  when,
  equals,
  always,
} from 'ramda'
import { RootState } from 'src/state/types'
import { PARTY_TYPES, PARTY_TAG_SORT_ORDER } from './constants'
import { $TSFixMe } from 'types/ts-migrate'
import {
  PartyAccount,
  TAccountTag,
  TBulkImportResult,
  TPartyDetails,
  TPartyEntity,
  TPartyState,
  TPartyTagOption,
  TPartyType,
} from './types'
import { walletApiSelectors } from '../wallet'

export type TMaybeId = string | null | undefined

const state = (state: RootState): RootState => state

export const partyState = (state: RootState): TPartyState => state.api.party

export const getEntities = createSelector(partyState, (ps): TPartyState['entities'] => ps.entities)

export const getParties = createSelector(
  getEntities,
  (entities): TPartyState['entities']['parties'] => entities.parties,
)

export const getSearchResults = createSelector(partyState, ps =>
  memoize((query: string): string[] => ps.entities.bySearchTerms[query] ?? []),
)

export const getPartyById = createSelector(
  partyState,
  ps =>
    (id: TMaybeId): TPartyEntity | false =>
      (id && ps.entities.parties[id]) || false,
)

export const isPartyLoading = createSelector(
  partyState,
  ps =>
    (id: TMaybeId): boolean =>
      !!(id && ps.pending?.[id]),
)

export const arePartiesLoading = createSelector(
  partyState,
  ps =>
    (ids: string[] = []): boolean =>
      ids.some(id => !!ps.pending?.[id]),
)

export const getCreatedPartyByInitiator = createSelector(
  partyState,
  ps =>
    (initiator: string): TPartyEntity | null =>
      ps.createdByInitiator?.[initiator] ?? null,
)

export const getPartyTypeById = createSelector(state, s => (id: TMaybeId): TPartyType | undefined => {
  const party = getPartyById(s)(id)
  if (!party) {
    return undefined
  }
  // Key presence is the only discriminator the payload carries - see TPartyPayload.
  if (party.person) {
    return 'person'
  }
  if (party.company) {
    return 'company'
  }
})

/**
 * Get party.person or party.company
 */
export const getPartyDetailsById = createSelector(state, s =>
  memoize((id: TMaybeId): TPartyDetails | undefined => {
    const party = getPartyById(s)(id)
    if (!party) {
      return undefined
    }
    return party.person ?? party.company
  }),
)

/**
 * Get a party details property value.
 * Assumes consistent property key i.e. person.emailAddress && company.emailAddress
 *
 * This is the funnel the ~20 `getPartyXById` selectors below are built on, so keying it on
 * `keyof TPartyDetails` is what types all of them.
 */
export const getPartyPropertyById = createSelector(
  state,
  s =>
    <K extends keyof TPartyDetails>(id: TMaybeId, prop: K): Exclude<TPartyDetails[K], undefined> | '' => {
      const partyDetails = getPartyDetailsById(s)(id)
      if (!partyDetails) {
        return ''
      }

      const value = partyDetails[prop]
      return value === undefined ? '' : (value as Exclude<TPartyDetails[K], undefined>)
    },
)

export const getPartiesByTag = createSelector(
  state,
  s => (tag: string) => Object.values(getParties(s)).filter(p => getPartyTagsById(s)(p.id).includes(tag)),
)

export const getAgencyParty = createSelector(state, s => {
  const partiesWithAgencyTag = getPartiesByTag(s)('Agency')
  return first(partiesWithAgencyTag.map(p => getPartyDetailsById(s)(p.id)))
})

export const getPartyFirstNameById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'firstName'),
)

export const getPartyLastNameById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'lastName'),
)

export const getPartyAddressById = createSelector(state, s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'address'))

export const getPartyNameById = createSelector(state, s => (id: TMaybeId): string => {
  const tradingAs = getPartyPropertyById(s)(id, 'tradingAs')
  const companyName = getPartyPropertyById(s)(id, 'companyName')
  if (tradingAs) {
    return tradingAs
  } else if (companyName) {
    return companyName
  }
  const firstName = getPartyPropertyById(s)(id, 'firstName')
  const lastName = getPartyPropertyById(s)(id, 'lastName')
  return `${firstName} ${lastName}`.trim()
})

export const getPartyCompanyNameById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'companyName'),
)
export const getPartyTradingAsOrCompanyNameById = createSelector(state, s => (id: TMaybeId) => {
  const tradingAs = getPartyPropertyById(s)(id, 'tradingAs')
  const companyName = getPartyPropertyById(s)(id, 'companyName')
  return tradingAs || companyName
})

export const getPartyEmailById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'emailAddress'),
)

export const getPartyAlternativeEmailById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'alternativeEmailAddress'),
)

export const getPartyTelNumberById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'cellNumber') || getPartyPropertyById(s)(id, 'telNumber'),
)

export const getPartyTaxNumberById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'taxNumber'),
)

export const getPartyVatNumberById = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'vatNumber'),
)

export const getPartyCompanyRegistration = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'companyRegistration'),
)

export const getPartyTagsById = createSelector(
  state,
  s =>
    (id: TMaybeId): string[] =>
      getPartyPropertyById(s)(id, 'tags') || [],
)

export const getPartyAccountsById = createSelector(
  state,
  s =>
    (id: TMaybeId): PartyAccount[] =>
      getPartyPropertyById(s)(id, 'accounts') || [],
)

export const getPartyAccountByAccountId = createSelector(
  state,
  s =>
    (id: TMaybeId, accountId: TMaybeId): PartyAccount | undefined =>
      getPartyAccountsById(s)(id).find(account => account.accountId === accountId),
)

export const getPartyUpdatedAt = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'updatedAt') || null,
)

export const getPersonsIdNumber = createSelector(state, s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'idNumber'))

export const getPersonsPassportNumber = createSelector(
  state,
  s => (id: TMaybeId) => getPartyPropertyById(s)(id, 'passport'),
)

export const getPartyBankDetails = createSelector(state, s => (id: TMaybeId) => ({
  bank: getPartyPropertyById(s)(id, 'bank'),
  accountNumber: getPartyPropertyById(s)(id, 'accountNumber'),
  accountType: getPartyPropertyById(s)(id, 'accountType'),
  accountName: getPartyPropertyById(s)(id, 'accountName'),
}))

export const partyHasBankDetails = createSelector(state, s => (id: TMaybeId) => {
  const bankDetails = getPartyBankDetails(s)(id)
  return Object.values(bankDetails).filter(b => !b).length === 0
})

export const getPartyFicaCompliance = createSelector(state, s => (id: TMaybeId): boolean | null => {
  const ficaCompliant = getPartyPropertyById(s)(id, 'ficaCompliant')
  return typeof ficaCompliant === 'boolean' ? ficaCompliant : null
})

export const getPartyTags = createSelector(getEntities, (entities): TPartyTagOption[] =>
  sortBy(
    pipe(
      (tag: TPartyTagOption) => tag.label,
      (label: string) => PARTY_TAG_SORT_ORDER.indexOf(label),
      when(equals(-1), always(999)),
    ),
    entities.partyTags ?? [],
  ),
)

export const getGroupedPartyTagsForSelect = createSelector(getPartyTags, partyTags => {
  const groupKeys = [...new Set(partyTags.map(tag => tag.label.split(/:(.+)/)[0]))]
  const allKeys = [...new Set(['EasyPay', ...groupKeys])]
  return pipe(
    Rmap((key: string) => ({ value: key, label: key })),
    filter(({ value }: TPartyTagOption) => value !== 'Agent'),
    sortBy(pipe(({ value }: TPartyTagOption) => PARTY_TAG_SORT_ORDER.indexOf(value), when(equals(-1), always(999)))),
  )(allKeys)
})

/**
 * The account a party holds against a portfolio.
 *
 * `preferredTag` is in practice a *required* match rather than a preference. This replaces
 * a ramda `ifElse(pipe(findOp, isEmpty), head, findOp)` whose `head` fallback was
 * effectively dead: ramda's `isEmpty(undefined)` is `false`, so a tag miss took the
 * `findOp` branch and returned `undefined` anyway. Kept as-is - deposits and payments are
 * routed off this, so turning the fallback on is a product call, not a refactor.
 */
export const getPartyAccountByPortfolioId = createSelector(
  state,
  s =>
    (partyId: TMaybeId, portfolioId: TMaybeId, preferredTag: TAccountTag): PartyAccount | undefined =>
      getPartyAccountsById(s)(partyId)
        .filter(account => account.portfolioId === portfolioId)
        .find(account => account.tag === preferredTag),
)

/**
 * Search selectors
 */

const groupPartyIdsByPartyType = createSelector(
  state,
  s => (partyIds: string[]) =>
    partyIds.reduce<{ people: string[]; companies: string[] }>(
      (acc, id) => {
        if (getPartyTypeById(s)(id) === 'person') {
          acc.people.push(id)
        } else {
          acc.companies.push(id)
        }
        return acc
      },
      { people: [], companies: [] },
    ),
)

export const partiesForSearchTerm = createSelector(state, s => (searchTerm: string) => {
  const partyIds = getSearchResults(s)(searchTerm)
  const { people, companies } = groupPartyIdsByPartyType(s)(partyIds)

  const shapeResult = (partyType: number) => (id: TMaybeId) => {
    const tags = getPartyTagsById(s)(id)
    const partyTag = head(tags)
    return {
      id,
      partyType,
      // partyTag: partyTag === 'agent' ? 'agency' : partyTag,
      partyTag,
      text: getPartyNameById(s)(id),
      secondaryText: getPartyEmailById(s)(id),
    }
  }
  return [people.map(shapeResult(PARTY_TYPES.PERSON)), companies.map(shapeResult(PARTY_TYPES.COMPANY))]
})

export type TPartySearchResult = {
  id: string
  text: string
  secondaryText: string
  partyTag: string
  partyType: number
}

export const getPartyForSearchResult = createSelector(
  state,
  s =>
    (id: string): TPartySearchResult | Record<string, never> => {
      const party = getPartyDetailsById(s)(id)
      if (!party) {
        return {}
      }
      const text = getPartyNameById(s)(id)
      const secondaryText = getPartyEmailById(s)(id)
      const partyTag = getPartyTagsById(s)(id)[0] ?? ''
      const partyType = getPartyTypeById(s)(id) === 'person' ? PARTY_TYPES.PERSON : PARTY_TYPES.COMPANY

      return { id, text, secondaryText, partyTag, partyType }
    },
)

/**
 * `lodash.memoize` keys on the *first* argument only, so every multi-argument selector
 * below has to spell out a resolver. Without one `('smith', ['Owner'])` and
 * `('smith', ['Tenant'])` share a cache entry, and the second call silently returns the
 * first one's results.
 */
const byQueryAndTags = (query: string, searchTags: string[] = []): string => `${query} ${searchTags.join(',')}`

export const partiesSearchResultsSplitByTag = createSelector(state, s =>
  memoize((query: string, searchTags: string[] = []) => {
    if (!query) {
      return []
    }

    const results = getSearchResults(s)(query)
    const searchResultSets = searchTags.map(searchTag => {
      const ids = results.filter(id => getPartyTagsById(s)(id).includes(searchTag))
      return ids.map(getPartyForSearchResult(s))
    })

    return searchResultSets.filter(searchResultSet => searchResultSet.length > 0)
  }, byQueryAndTags),
)

type AccountResult = {
  id: string
  text: string
  secondaryText: string
  partyTag: TAccountTag
  portfolioId?: string | null
  paymentReference?: string
}

export const partiesSearchResultsSplitByAccount = createSelector(state, s => (query: string) => {
  const results = getSearchResults(s)(query)

  // Split by tags
  const getPartyResults = (party: $TSFixMe): $TSFixMe => {
    const tags = getPartyTagsById(s)(party.id)
    return tags.map((tag: string) => ({
      id: party.id,
      text: getPartyNameById(s)(party.id),
      partyTag: tag,
      secondaryText: `${tag} - no attached lease`,
    }))
  }

  // Check for consolidated balance
  const getConsolidatedBalanceResult = (partyId: string): $TSFixMe => {
    const consolidatedBalanceAsset = walletApiSelectors.getConsolidatedBalanceAsset(s)(partyId)
    if (!consolidatedBalanceAsset) {
      return null
    }
    // The reference comes from the wallet's consolidatedBalance (accountReference) - the authoritative
    // source - rather than the party accounts, whose shape varies by endpoint.
    return {
      id: partyId,
      text: getPartyNameById(s)(partyId),
      partyTag: 'Owner',
      secondaryText: 'Owner - no attached lease',
      paymentReference: walletApiSelectors.getConsolidatedBalanceReference(s)(partyId),
      isConsolidatedBalance: true,
    }
  }

  // Split by accounts
  const getAccountResults = (p: $TSFixMe): $TSFixMe => {
    const results: AccountResult[] = getPartyAccountsById(s)(p.id).map(account => ({
      id: account.partyId,
      text: getPartyNameById(s)(p.id),
      partyTag: account.tag,
      paymentReference: account.paymentReference,
      secondaryText: account.propertyAddress ? `${account.tag} @ ${account.propertyAddress}` : account.tag,
      portfolioId: account.portfolioId,
    }))

    const filteredResults = pipe(
      groupBy((result: AccountResult) => result.paymentReference || ''),
      values,
      Rmap((group: AccountResult[]) => {
        const tags = group.map(g => g.partyTag)
        return filter((item: AccountResult) => {
          return tags.includes('Application') && tags.includes('Tenant') ? item.partyTag !== 'Application' : true
        })(group)
      }),
      flatten,
    )(results)

    return filteredResults
  }

  // Either split by accounts OR party tags
  return [
    pipe(
      Rmap((id: string) => {
        const party = getPartyById(s)(id)
        const accounts = getPartyAccountsById(s)(id)
        const consolidatedBalanceResult = getConsolidatedBalanceResult(id)

        // When a consolidated balance exists, the unattached rows are redundant - both the tag-based
        // "Owner - no attached lease" row and the account-based unattached Consolidated account are
        // merged into the consolidated entry, so drop them from both result sets.
        const isUnattachedConsolidated = (result: $TSFixMe) =>
          !result.portfolioId && (result.partyTag === 'Owner' || result.partyTag === 'Consolidated')
        const dropUnattachedConsolidated = (rows: $TSFixMe) =>
          consolidatedBalanceResult ? filter((row: $TSFixMe) => !isUnattachedConsolidated(row), rows) : rows

        const partyResults = dropUnattachedConsolidated(getPartyResults(party))
        const accountResults = dropUnattachedConsolidated(getAccountResults(party))
        const allResults = accounts.length > 0 ? [...partyResults, ...accountResults] : partyResults
        return consolidatedBalanceResult ? [consolidatedBalanceResult, ...allResults] : allResults
      }),
      flatten,
    )(results),
  ]
})

type TPartySearchResultOrEmpty = ReturnType<ReturnType<typeof getPartyForSearchResult>>

// Persistent cache keyed on the `parties` slice reference. The previous
// `createSelector([state], ...)` recomputed on every dispatch (the whole store is
// the memo input) and returned a fresh `memoize` fn each time, so callers received a
// NEW array on every action. That defeated referential-equality bailouts in
// `connect`/`useSelector`, re-rendering the commission form and its open PartySearch
// dropdown on every dispatch (render churn in the 500s while typing an agent name).
// The derivation itself only reads party data, so keying on `getParties(s)` returns a
// stable reference until party data actually changes, per tag combination.
let partiesResultsByTagsCache: {
  parties: TPartyState['entities']['parties'] | undefined
  byTags: Map<string, TPartySearchResultOrEmpty[]>
} = {
  parties: undefined,
  byTags: new Map(),
}

export const partiesResultsByTags =
  (s: RootState) =>
  (searchTags: string[] = []) => {
    const parties = getParties(s)
    if (partiesResultsByTagsCache.parties !== parties) {
      partiesResultsByTagsCache = { parties, byTags: new Map() }
    }

    const key = searchTags.join(',')
    const cached = partiesResultsByTagsCache.byTags.get(key)
    if (cached) {
      return cached
    }

    const results = Object.keys(parties)
    let searchResults: TPartySearchResultOrEmpty[] = []
    searchTags.forEach((tag: string) => {
      const ids = results.filter(id => getPartyTagsById(s)(id).includes(tag))
      searchResults = concat(searchResults, ids.map(getPartyForSearchResult(s)))
    })

    partiesResultsByTagsCache.byTags.set(key, searchResults)
    return searchResults
  }

export const partiesSearchResultsByTags = createSelector(state, s =>
  memoize((query: string, searchTags: string[] = []) => {
    if (!query) {
      return []
    }

    const results = getSearchResults(s)(query)

    let searchResults: TPartySearchResultOrEmpty[] = []
    searchTags.forEach((tag: string) => {
      const ids = results.filter(id => getPartyTagsById(s)(id).includes(tag))
      searchResults = concat(searchResults, ids.map(getPartyForSearchResult(s)))
    })

    return searchResults
  }, byQueryAndTags),
)

export const getSuspenseAccountFuzzyMatches = createSelector(getEntities, entities =>
  memoize((reference: string): $TSFixMe => pathOr([], ['suspenseAccountFuzzyMatches', reference], entities)),
)

export const getBulkImportResult = createSelector(partyState, (ps): TBulkImportResult | null => ps.bulkImportResult)
